import {Trans} from '@lingui/macro';
import {DateTime, Interval} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useDoNotDisturb} from '../../../../../srcCommon/app/doNotDisturbHooks';
import {TimeFormatsEnum} from '../../../../helpers/dateTimeHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {updateShouldMuteNotificationsAction} from '../../../../store/data/preferences/preferencesActions';
import {
  getShouldMuteNotifications,
  getTimeFormat,
} from '../../../../store/data/preferences/preferencesSelectors';
import {saveUserAction} from '../../../../store/data/teammates/teammatesActions';
import {TimeComboBox} from '../../../common/dateTime/timeComboBox';
import {Disabled} from '../../../common/disabled';
import {QuickSettingsDropdown} from '../../../common/dropdowns/quickSettingsDropdown';
import {QuickSettingsDropdownItem} from '../../../common/dropdowns/quickSettingsDropdownItem';
import {errorFallbackMessage} from '../../../errors/errorFallbackMessage';

/*
 * Props.
 */

interface AvailabilityDropdownProps extends DropdownProps {
  teammate: TeammateModel;
}

/*
 * Styles.
 */

const StyledTimeDropdownDiv = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr auto 1fr auto;
  align-items: center;
  margin: -9px 12px -5px 12px;

  color: ${(p) => p.theme.greys.shade50};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const AvailabilityDropdown: FC<AvailabilityDropdownProps> = ({anchor, onRequestClose, teammate}) => {
  const shouldMuteNotifications = useAppSelector(getShouldMuteNotifications);
  const doNotDisturb = useDoNotDisturb();
  const timeFormat = useAppSelector(getTimeFormat);

  const isMuteAvailable = teammate.isAvailable;
  const isDoNotDisturbAvailable = teammate.isAvailable && !shouldMuteNotifications;

  const dispatch = useAppDispatch();

  const onUserIsAvailableChange = (isAvailable: boolean) => dispatch(saveUserAction({isAvailable}));

  return (
    <QuickSettingsDropdown anchor={anchor} onRequestClose={onRequestClose}>
      <QuickSettingsDropdownItem
        title={<Trans id="availability-ooo-title">Out of Office</Trans>}
        description={
          <Trans id="availability-ooo-description">Mute notifications, unassign new messages</Trans>
        }
        isChecked={!teammate.isAvailable}
        onChange={(checked) => onUserIsAvailableChange(!checked)}
      />
      <QuickSettingsDropdownItem
        title={<Trans id="availability-mute-title">Do Not Disturb</Trans>}
        description={<Trans id="availability-mute-description">Mute notifications</Trans>}
        isDisabled={!isMuteAvailable}
        isChecked={shouldMuteNotifications}
        onChange={(checked) => dispatch(updateShouldMuteNotificationsAction(checked))}
      />
      <QuickSettingsDropdownItem
        interactionId="availability-dnd"
        title={<Trans id="availability-dnd-title">Do Not Disturb Schedule</Trans>}
        description={<Trans id="availability-dnd-description">Set a daily time to mute notifications</Trans>}
        isDisabled={!isDoNotDisturbAvailable}
        isChecked={doNotDisturb.isEnabled}
        onChange={doNotDisturb.onIsEnabledChange}
      />
      {renderDoNotDisturbTimes(
        timeFormat,
        isDoNotDisturbAvailable && doNotDisturb.isEnabled,
        doNotDisturb.interval,
        doNotDisturb.onStartTimeChange,
        doNotDisturb.onEndTimeChange,
      )}
    </QuickSettingsDropdown>
  );
};

/*
 * Helpers.
 */

const renderDoNotDisturbTimes = (
  timeFormat: TimeFormatsEnum,
  isEnabled: boolean,
  interval: Interval | undefined,
  onStartTimeChange: HandlerOf<DateTime>,
  onEndTimeChange: HandlerOf<DateTime>,
) => {
  if (!interval) {
    return errorFallbackMessage;
  }

  return (
    <Disabled isDisabled={!isEnabled}>
      <StyledTimeDropdownDiv>
        <TimeComboBox
          interactionId="availability-dnd-start"
          timeFormat={timeFormat}
          selectedTime={interval.start}
          onTimeSelect={(time) => time && onStartTimeChange(time)}
        />
        <Trans id="availability-dnd-to">to</Trans>
        <TimeComboBox
          interactionId="availability-dnd-end"
          timeFormat={timeFormat}
          selectedTime={interval.end}
          onTimeSelect={(time) => time && onEndTimeChange(time)}
        />
        <span data-testid="availability-dnd-timezone">{interval.start.offsetNameShort}</span>
      </StyledTimeDropdownDiv>
    </Disabled>
  );
};
